import React, { useState } from "react";
import timezones from "../../../setup/config/timezone.json";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
} from "@mui/icons-material";

const ITEM_TYPES = [
  { label: "String", value: "string" },
  { label: "Number", value: "number" },
  { label: "Boolean", value: "boolean" },
  { label: "Date", value: "date" },
  { label: "Array", value: "array" },
  { label: "Object", value: "object" },
];

const DynamicField = ({
  field,
  onChange,
  onRemove,
  isEditMode,
  depth = 0,
  path = "",
  togglePasswordVisibility,
  passwordVisibility,
  categoryFields = [],
  onStartScheduler,
  onStopScheduler,
}) => {
  const isMandatory = [
    "clientId",
    "channel",
    "folderPath",
    "dbConnectionURL",
    "apiLink",
  ].includes(field.key);
  const isClientId = field.key === "clientId";
  const shouldDisableField = isEditMode && isClientId;

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    let newValue = "";
    if (newType === "boolean") newValue = false;
    if (newType === "array") newValue = [];
    if (newType === "object") newValue = [];
    if (newType === "number") newValue = 0;
    if (newType === "date") newValue = new Date().toISOString().split("T")[0];

    onChange({ ...field, type: newType, value: newValue });
  };

  const handleAddChild = () => {
    const newValue = [
      ...(Array.isArray(field.value) ? field.value : []),
      { key: "", value: "", type: "string" },
    ];
    onChange({ ...field, value: newValue });
  };

  const handleChildChange = (childIndex, newChildField) => {
    const newValue = [...field.value];
    newValue[childIndex] = newChildField;
    onChange({ ...field, value: newValue });
  };

  const handleRemoveChild = (childIndex) => {
    const newValue = field.value.filter((_, i) => i !== childIndex);
    onChange({ ...field, value: newValue });
  };

  const renderValueInput = (inline = false) => {

    if (field.type === "boolean") {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(field.value)}
              onChange={(e) => onChange({ ...field, value: e.target.checked })}
              color="primary"
              size="small"
              disabled={shouldDisableField}
            />
          }
          label={field.value ? "True" : "False"}
          sx={{ mr: 2 }}
        />
      );
    }

    if (field.type === "date") {
      return (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          type="date"
          value={field.value || ""}
          onChange={(e) => onChange({ ...field, value: e.target.value })}
          disabled={shouldDisableField}
          InputProps={{
            style: { fontSize: "0.9rem" },
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
    }

    if (field.type === "object" || field.type === "array") {
        if (inline) return null;
        
        return (
            <Box sx={{ width: "100%", mt: 0.5, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
               {field.type === "object" && (
                   <>
                    {(Array.isArray(field.value) ? field.value : []).map(
                        (childField, cIndex) => (
                        <DynamicField
                            key={cIndex}
                            field={childField}
                            onChange={(newField) => handleChildChange(cIndex, newField)}
                            onRemove={() => handleRemoveChild(cIndex)}
                            isEditMode={isEditMode}
                            depth={depth + 1}
                            path={`${path}.${cIndex}`}
                            togglePasswordVisibility={togglePasswordVisibility}
                            passwordVisibility={passwordVisibility}
                        />
                        )
                    )}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddChild}
                        sx={{ mt: 0.5, textTransform: "none", color: '#666' }}
                    >
                        Add Field
                    </Button>
                   </>
               )}
               
               {field.type === "array" && (
                   <>
                    {(Array.isArray(field.value) ? field.value : []).map(
                        (val, vIndex) => (
                        <Box key={vIndex} sx={{ display: "flex", mb: 0.5, gap: 1, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={val}
                                placeholder={`Item ${vIndex}`}
                                onChange={(e) => {
                                    const newValue = [...field.value];
                                    newValue[vIndex] = e.target.value;
                                    onChange({ ...field, value: newValue });
                                }}
                                InputProps={{ style: { fontSize: '0.9rem', padding: 0 } }}
                            />
                            <IconButton
                                size="small"
                                color="default"
                                onClick={() => {
                                    const newValue = field.value.filter((_, i) => i !== vIndex);
                                    onChange({ ...field, value: newValue });
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        )
                    )}
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                        const newValue = [...(field.value || []), ""];
                        onChange({ ...field, value: newValue });
                        }}
                        sx={{ mt: 0.5, textTransform: "none", color: '#666' }}
                    >
                        Add Item
                    </Button>
                   </>
               )}
            </Box>
        );
    }

    if (field.key === "timeZone" || field.key === "timezone") {
      const hasValue = timezones.some(tz => tz.zone === field.value);
      return (
        <Select
          fullWidth
          size="small"
          value={field.value || ""}
          onChange={(e) => onChange({ ...field, value: e.target.value })}
          disabled={shouldDisableField}
          sx={{ fontSize: "0.9rem" }}
        >
          {field.value && !hasValue && (
            <MenuItem value={field.value}>{field.value}</MenuItem>
          )}
          {timezones.map((tz) => (
            <MenuItem key={tz.zone} value={tz.zone}>
              {tz.gmt} {tz.name} ({tz.zone})
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (field.key === "campaignSchedulerType") {
      const schedulerEnableField = categoryFields.find(f => f.key === 'canCampaignSchedulerEnable');
      const isSchedulerEnabled = schedulerEnableField ? Boolean(schedulerEnableField.value) : false;

      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
          <Select
            fullWidth
            size="small"
            value={field.value || "node"}
            onChange={(e) => onChange({ ...field, value: e.target.value })}
            disabled={shouldDisableField}
            sx={{ fontSize: "0.9rem", flex: 1 }}
          >
            <MenuItem value="node">Node</MenuItem>
            <MenuItem value="temporal">Temporal (Kafka/Java)</MenuItem>
          </Select>
          {isEditMode && field.value === 'node' && (
            <Tooltip title={isSchedulerEnabled ? "Stop Scheduler" : "Start Scheduler"}>
              <IconButton 
                size="small" 
                onClick={isSchedulerEnabled ? onStopScheduler : onStartScheduler}
                sx={{ 
                  color: isSchedulerEnabled ? '#d32f2f' : '#2e7d32',
                  border: '1px solid',
                  borderColor: isSchedulerEnabled ? '#d32f2f' : '#2e7d32',
                  '&:hover': {
                    backgroundColor: isSchedulerEnabled ? 'rgba(211, 47, 47, 0.04)' : 'rgba(46, 125, 50, 0.04)'
                  }
                }}
              >
                {isSchedulerEnabled ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }
    const isSecret =
      field.key && (
      field.key.toLowerCase().includes("key") ||
      field.key.toLowerCase().includes("secret") ||
      field.key.toLowerCase().includes("token") );
    const showPassword =
      passwordVisibility && passwordVisibility[`${path}`];

    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        type={
          field.type === "number" ? "number" : isSecret && !showPassword ? "password" : "text"
        }
        value={field.value}
        onChange={(e) => onChange({ ...field, value: e.target.value })}
        disabled={shouldDisableField}
        multiline={
          field.key === "private_key" || field.key === "firebasePrivateKey"
        }
        rows={
          field.key === "private_key" || field.key === "firebasePrivateKey"
            ? 3
            : 1
        }
        InputProps={{
          style: { fontSize: '0.9rem' },
          endAdornment: isSecret ? (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility(`${path}`)}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        placeholder="Value"
      />
    );
  };

  return (
    <Box
      className="dynamic-field-row"
      sx={{
        mb: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        fontFamily: 'monospace'
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
        <Box sx={{ flex: 1, minWidth: '150px' }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={field.key}
            onChange={(e) => onChange({ ...field, key: e.target.value })}
            disabled={field.isStatic || shouldDisableField}
            placeholder="Key"
            InputProps={{ style: { fontSize: '0.9rem', fontWeight: 500 } }}
          />
        </Box>

        <Typography variant="body1" sx={{ color: '#888', fontWeight: 'bold' }}>:</Typography>

        <Box sx={{ flex: 2 }}>
           {(field.type !== 'object' && field.type !== 'array' && field.type !== 'date') ? renderValueInput(true) : 
            <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic', pl: 1 }}>
                {field.type === 'object' ? '{Object}' : field.type === 'array' ? '[Array]' : '(Date)'}
            </Typography>
           }
        </Box>
        
        <Box sx={{ minWidth: 100 }}>
             <Select
              value={field.type || "string"}
              onChange={handleTypeChange}
              disabled={field.isStatic || shouldDisableField}
              variant="standard"
              disableUnderline
              displayEmpty
              size="small"
              sx={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: '12px', 
                  padding: '2px 8px',
                  '& .MuiSelect-select': { paddingRight: '24px !important', paddingTop: '2px', paddingBottom: '2px'  } 
              }}
            >
              {ITEM_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value} dense>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
        </Box>

        {!field.isStatic && (
            <Tooltip title="Remove">
              <IconButton color="default" onClick={onRemove} size="small" sx={{ opacity: 0.6, '&:hover':{opacity:1} }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
        )}
      </Box>

      {(field.type === 'object' || field.type === 'array' || field.type === 'date') && (
         <Box sx={{ width: "100%" }}>
            {renderValueInput(false)}
         </Box>
      )}
    </Box>
  );
};

export default DynamicField;
